import {Box, Card, CardContent, Container, Grid, Stack, Typography} from '@mui/material';
import {NavLink} from 'react-router';
import Link, {LinkProps} from '@mui/material/Link';
import {Trans} from '@lingui/react/macro';
import {t} from '@lingui/core/macro';
import {useTitle} from 'react-use';
import useAuth, {useStateSession} from '../hooks/useAuth';
import ActiveCuttings from '../timber/components/cutting/CuttingList/ActiveCuttings.tsx';
import ProviderOverviewTab from '../timber/components/provider/ProviderDetail/ProviderOverviewTab';
import {useGetProviderQuery} from '../timber/apis/Provider.api.ts';
import {PrimaryProvider} from '../timber/types/models/Provider';
import GradientCircularProgress from '../share/components/progress/GradientCircularProgress.tsx';
import {ID} from '../types/global.d';
import {Session} from '../app/slices/sessionSlice';

interface LinkRouterProps extends LinkProps {
    to: string;
    replace?: boolean;
    viewTransition?: boolean;
}

function LinkRouter(props: LinkRouterProps) {
    return <Link {...props} component={NavLink as any}/>;
}

/**
 * The scope groups the session is bound to for a context.
 *
 * The scope contexts arrive from the token as a plain object, but they are
 * typed as a Map, so they have to be read as what they are.
 */
const scopeGroupsFor = (session: Session, context: string): ID[] => {
    const scopeContexts = session?.scopeGroupContext?.scopeContexts as unknown as Record<string, ID[]>;
    return scopeContexts?.[context] ?? [];
};

type QuickLink = {
    path: string;
    label: string;
    permission?: string;
};

/**
 * The links a manager reaches for. Each one is hidden when the user has no
 * permission for it, so the page never offers a door that leads to a 403.
 */
const QUICK_LINKS: QuickLink[] = [
    {path: '/dashboard/cuttings', label: 'Cuttings', permission: 'applications/timber/entities/Cuttings/list/view'},
    {path: '/dashboard/shipments', label: 'Shipments', permission: 'applications/timber/entities/Shipments/list/view'},
    {path: '/dashboard/timber-storages', label: 'Timber Storages', permission: 'applications/timber/entities/TimberStorages/list/view'},
    {path: '/dashboard/logs', label: 'Logs', permission: 'applications/timber/entities/Logs/list/view'},
    {path: '/dashboard/cordwoods', label: 'Cordwoods', permission: 'applications/timber/entities/Cordwoods/list/view'},
    {path: '/dashboard/providers', label: 'Providers', permission: 'applications/timber/entities/Providers/list/view'},
    {path: '/dashboard/buyers', label: 'Buyers', permission: 'applications/timber/entities/Buyers/list/view'},
    {path: '/dashboard/reports', label: 'Reports'},
    {path: '/dashboard/history', label: 'History'},
];

const QuickLinks = () => {
    const session = useStateSession();

    const links = QUICK_LINKS.filter(link => !link.permission || session.hasPermission('', link.permission));

    if (links.length === 0) {
        return null;
    }

    return (
        <Box data-testid="Dashboard-home-links">
            <Typography variant="h6" sx={{marginBottom: '1em'}}>
                <Trans>Go to</Trans>
            </Typography>
            <Grid container spacing={2}>
                {links.map(link => (
                    <Grid key={link.path} size={{xs: 6, md: 3}}>
                        <Card variant="outlined" sx={{height: '100%'}}>
                            <LinkRouter to={link.path} viewTransition={true} underline="none"
                                       sx={{display: 'block', color: 'inherit'}}>
                                <CardContent>
                                    <Typography variant="subtitle1">{link.label}</Typography>
                                </CardContent>
                            </LinkRouter>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};

/**
 * The home of a user who is bound to one provider: the state of their own
 * company, with nothing else on the page.
 */
const ProviderHome = () => {
    const {data: provider, isLoading, isError} = useGetProviderQuery(PrimaryProvider.fromPath('scoped'));

    if (isLoading) {
        return (
            <Stack sx={{alignItems: 'center', padding: '2em'}} data-testid="Dashboard-home-loading">
                <GradientCircularProgress/>
            </Stack>
        );
    }

    if (isError || !provider) {
        return (
            <Typography variant="body2" data-testid="Dashboard-home-provider-missing">
                <Trans>The provider of this account could not be loaded.</Trans>
            </Typography>
        );
    }

    return (
        <Stack spacing={3} data-testid="Dashboard-home-provider">
            <Typography variant="h5" component="div">
                {provider.name}
            </Typography>
            <ProviderOverviewTab providerId={provider.id}/>
        </Stack>
    );
};

/**
 * The home of everybody else: what is going on in the installation right now,
 * and the doors to the parts of it.
 */
const InstallationHome = () => {
    const session = useStateSession();

    const canSeeCuttings = session.hasPermission('', 'applications/timber/entities/Cuttings/list/view');

    return (
        <Stack spacing={3} data-testid="Dashboard-home-installation">
            {canSeeCuttings && <ActiveCuttings/>}
            <QuickLinks/>
        </Stack>
    );
};

/**
 * The landing page of the dashboard.
 *
 * The dashboard shell used to have no index route, so `/` and `/dashboard`
 * rendered an empty content area. This page fills it, and it fills it with
 * whatever the signed-in user is here for.
 */
const DashboardHomePage = () => {
    const {session, isAuthenticated} = useAuth();

    // The dashboard is the front door of the application, so it is called by
    // the name of the application. Every other page is called by what it is.
    useTitle(t`Timberstorage`);

    // A user bound to a provider scope group is only ever meant to see that
    // provider, so the home page is that provider and nothing else.
    const isProviderScoped = isAuthenticated
        && scopeGroupsFor(session, 'Providers').length > 0
        && session.hasContextPermission('Providers', 'applications/timber/entities/Providers/read/view');

    return (
        <Container maxWidth="lg" data-testid="Dashboard-home">
            <Box sx={{pt: 2, pb: 2}}>
                <Typography variant="h4" component="h1" data-testid="Dashboard-home-title">
                    {isProviderScoped
                        ? <Trans>My provider</Trans>
                        : <Trans>Dashboard</Trans>}
                </Typography>
            </Box>

            {!isAuthenticated
                ? <Typography variant="body2"><Trans>Sign in to see the state of the timber.</Trans></Typography>
                : isProviderScoped
                    ? <ProviderHome/>
                    : <InstallationHome/>
            }
        </Container>
    );
};

export default DashboardHomePage;
